<template>
  <section
    id="study"
    ref="revealTarget"
    class="section study-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="page-container">
      <div class="section-heading-row">
        <div class="section-head">
          <span class="section-kicker">SCIENCE EDUCATION</span>
          <h2>把热带植物科研带进真实课堂</h2>
        </div>
        <nuxt-link v-if="studyPath" class="section-more" :to="studyPath">
          查看更多
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <ui-expandable-gallery
        v-if="visibleItems.length > 0"
        :items="visibleItems"
        :default-index="0"
        class="study-gallery"
        aria-label="研学内容"
      >
        <template #default="{ item, active }">
          <nuxt-link
            class="study-card"
            :class="{ 'is-active': active }"
            :to="articlePath(item)"
            @click="handleCardClick($event, active)"
          >
            <div class="study-media">
              <home-media :source="item.cover_url" :alt="item.title" loading="lazy" />
            </div>
            <div class="study-active-copy">
              <span class="study-label">{{ item.category.name }}</span>
              <h3>{{ item.title }}</h3>
              <p v-if="studySummary(item)">{{ studySummary(item) }}</p>
              <span class="study-cta">
                立即查看
                <i class="i-custom-enter" aria-hidden="true"></i>
              </span>
            </div>
            <div class="study-collapsed-copy">
              <span class="study-label">{{ item.category.name }}</span>
              <h3>{{ item.title }}</h3>
              <p v-if="studySummary(item)">{{ studySummary(item) }}</p>
              <span class="study-card-arrow" aria-hidden="true">
                <i class="i-custom-enter"></i>
              </span>
            </div>
          </nuxt-link>
        </template>
      </ui-expandable-gallery>
      <div v-else class="study-empty">暂无研学内容</div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { ArticleItem } from '~/types/site-api'

  import {
    findArticleCategory,
    getArticleCategoryEntryRoute,
    getArticlePath,
  } from '~/utils/content'

  defineOptions({
    name: 'HomeStudy',
  })

  const props = withDefaults(
    defineProps<{
      items?: ArticleItem[]
    }>(),
    {
      items: () => [],
    }
  )

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const visibleItems = computed(() => props.items.slice(0, 4))
  const { data: categoryResponse } = useArticleCategories()
  const studyPath = computed(() => {
    const code = visibleItems.value[0]?.category.code
    const match = code ? findArticleCategory(categoryResponse.value?.data ?? [], code) : undefined
    return match ? getArticleCategoryEntryRoute(match.root).path : undefined
  })

  function articlePath(article: ArticleItem) {
    return getArticlePath(article)
  }

  function studySummary(article: ArticleItem) {
    return article.summary || article.subtitle
  }

  function handleCardClick(event: MouseEvent, active: boolean) {
    if (!active) event.preventDefault()
  }
</script>

<style scoped lang="scss">
  .study-section {
    background: var(--surface);
  }

  .section-heading-row {
    display: flex;
    gap: 24px;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 48px;
  }

  .section-head {
    max-width: 780px;
    margin-bottom: 0;
  }

  .section-head h2 {
    margin: 0;
    font-size: clamp(28px, 3.2vw, 44px);
    font-weight: 620;
    line-height: 1.18;
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: var(--accent);
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .section-more {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
    align-items: center;
    min-height: 44px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--more);
    white-space: nowrap;
    cursor: pointer;
  }

  .section-more i,
  .study-cta i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover {
    color: var(--brand-strong);
  }

  .section-more:hover i,
  .study-card:hover .study-cta i {
    transform: translateX(4px);
  }

  .study-gallery {
    --eg-active: 3.4;
    --eg-inactive: 1;

    gap: 24px;
    height: 460px;
  }

  .study-gallery :deep(> div) {
    height: 100%;
    background: var(--surface-soft);
    border-radius: var(--radius-md);
  }

  .study-card {
    position: relative;
    display: block;
    width: 100%;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    color: var(--ink);
    cursor: pointer;
    background: var(--surface-soft);
    border-radius: var(--radius-md);
    isolation: isolate;
  }

  .study-card::after {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    content: '';
    background: linear-gradient(180deg, rgb(195 20 30 / 64%), rgb(153 0 11 / 84%));
    opacity: 0;
    transition: opacity var(--normal) var(--ease);
  }

  .study-media {
    position: absolute;
    inset: 0;
    opacity: 0;
    transition:
      opacity var(--normal) var(--ease),
      transform 520ms var(--ease);
  }

  .study-active-copy,
  .study-collapsed-copy {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    min-width: 0;
    transition:
      opacity 180ms var(--ease),
      visibility 180ms var(--ease);
  }

  .study-active-copy {
    visibility: hidden;
    padding: 48px 54px 40px;
    color: #fff;
    opacity: 0;
  }

  .study-collapsed-copy {
    visibility: visible;
    padding: 48px 44px 36px;
    opacity: 1;
  }

  .study-card.is-active::after,
  .study-card.is-active .study-media,
  .study-card.is-active .study-active-copy {
    visibility: visible;
    opacity: 1;
  }

  .study-card.is-active .study-media {
    transform: scale(1.01);
  }

  .study-card.is-active .study-collapsed-copy {
    visibility: hidden;
    opacity: 0;
  }

  .study-card:hover .study-media :deep(.home-media) {
    transform: scale(1.025);
  }

  .study-media :deep(.home-media) {
    transition: transform 520ms var(--ease);
  }

  .study-label {
    align-self: flex-start;
    padding: 7px 13px;
    font-size: 14px;
    font-weight: 620;
    line-height: 1.4;
    color: var(--accent);
    white-space: nowrap;
    background: #fff5f5;
    border-radius: 999px;
  }

  .study-active-copy .study-label {
    background: #fff;
  }

  .study-active-copy h3 {
    max-width: 820px;
    margin: 24px 0 0;
    font-size: 28px;
    font-weight: 620;
    line-height: 1.35;
    color: #fff;
  }

  .study-active-copy p {
    display: -webkit-box;
    max-width: 760px;
    margin: 22px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 3;
    font-size: 16px;
    line-height: 1.8;
    color: rgb(255 255 255 / 90%);
    -webkit-box-orient: vertical;
  }

  .study-cta {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    align-self: flex-start;
    justify-content: center;
    min-width: 168px;
    padding: 16px 26px;
    margin-top: auto;
    font-size: 16px;
    line-height: 1.5;
    color: #fff;
    border: 1px solid rgb(255 255 255 / 92%);
    border-radius: 999px;
  }

  .study-collapsed-copy h3 {
    display: -webkit-box;
    margin: 22px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 3;
    font-size: 22px;
    font-weight: 620;
    line-height: 1.45;
    -webkit-box-orient: vertical;
  }

  .study-collapsed-copy p {
    display: -webkit-box;
    margin: 22px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 4;
    font-size: 15px;
    line-height: 1.7;
    color: var(--muted);
    -webkit-box-orient: vertical;
  }

  .study-card-arrow {
    display: grid;
    place-items: center;
    width: 50px;
    height: 50px;
    margin-top: auto;
    color: var(--accent);
    background: var(--surface);
    border-radius: 50%;
  }

  .study-card-arrow i {
    width: 22px;
    height: 22px;
  }

  .study-empty {
    display: grid;
    place-items: center;
    min-height: 280px;
    color: var(--muted);
    background: var(--surface-soft);
    border-radius: var(--radius-md);
  }

  @media (max-width: 767px) {
    .section-heading-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px 16px;
      align-items: center;
      margin-bottom: 36px;
    }

    .section-head {
      display: contents;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .section-head h2 {
      grid-row: 2;
      grid-column: 1 / -1;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      margin: 0;
    }

    .study-gallery {
      --eg-active: 0;
      --eg-inactive: 0;
      --eg-item-shrink: 0;
      --eg-active-basis: 0%;
      --eg-inactive-basis: 0%;

      gap: 12px;
      width: 100vw;
      height: auto;
      padding-inline: 24px;
      padding-bottom: 8px;
      margin-inline: calc((100% - 100vw) / 2);
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
    }

    .study-gallery::-webkit-scrollbar {
      display: none;
    }

    .study-gallery :deep(> div) {
      flex: 0 0 calc(100vw - 48px);
      width: calc(100vw - 48px);
      min-width: calc(100vw - 48px);
      height: 420px;
      scroll-snap-align: center;
      scroll-snap-stop: always;
      transition: none;
    }

    .study-active-copy,
    .study-collapsed-copy {
      padding: 28px 24px 24px;
    }

    .study-active-copy h3 {
      display: -webkit-box;
      margin-top: 20px;
      overflow: hidden;
      -webkit-line-clamp: 3;
      font-size: 22px;
      -webkit-box-orient: vertical;
    }

    .study-active-copy p {
      margin-top: 16px;
      -webkit-line-clamp: 3;
      font-size: 14px;
      line-height: 1.7;
    }

    .study-collapsed-copy h3 {
      -webkit-line-clamp: 3;
      font-size: 21px;
    }

    .study-collapsed-copy p {
      -webkit-line-clamp: 4;
      font-size: 14px;
    }

    .study-cta {
      min-width: 146px;
      padding: 13px 22px;
      font-size: 15px;
    }
  }
</style>
